import React from "react"
import { Image } from "expo-image"
import { View, Pressable, StyleSheet, ScrollView, Platform } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { type AnalysisCompType } from "@/generated/graphql"
import { H6 } from "@/components/standard/BFText"

interface CompDetailImageProps {
  uri: string
  index: number
  imageCount: number
  containerWidth: number
  onPress: () => void
}

const CompDetailImage: React.FC<CompDetailImageProps> = ({
  uri,
  index,
  imageCount,
  containerWidth,
  onPress,
}) => {
  return (
    <Pressable testID={`image-${index}`} onPress={onPress} className="p-4">
      <View className="relative">
        <Image
          source={{ uri }}
          style={[
            styles.image,
            {
              width: containerWidth - 8,
              height: ((containerWidth - 8) * 9) / 16,
            },
          ]}
          contentFit="cover"
        />
        <View className="absolute top-4 right-4 px-4 py-1 rounded-[2px] overflow-hidden bg-lightest/60 justify-center items-center">
          <H6 className="text-black web:mt-px">
            {index + 1}/{imageCount}
          </H6>
        </View>
      </View>
    </Pressable>
  )
}

interface CompDetailImagesListProps {
  comp: AnalysisCompType
  onDragStateChange: (isDragging: boolean) => void
  onImagePress: (index: number) => void
}

const CompDetailImagesList: React.FC<CompDetailImagesListProps> = ({
  comp,
  onDragStateChange,
  onImagePress,
}) => {
  const [width, setWidth] = React.useState(400)
  if (!comp.arvcomp) {
    return null
  }

  const insets = useSafeAreaInsets()
  const images: string[] = comp.arvcomp.media || []
  if (images.length === 0 && comp.arvcomp.heroImage) {
    images.push(comp.arvcomp.heroImage)
  }

  return (
    <ScrollView
      className={"flex-1 mb-6"}
      onLayout={({ nativeEvent }) => {
        setWidth(nativeEvent.layout.width)
      }}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={[
        styles.scrollViewContainer,
        { paddingBottom: insets.bottom },
      ]}
      // scroll w/in Image list should always bring back the parent indicator
      onScrollBeginDrag={() => {
        onDragStateChange(false)
      }}
      onScrollEndDrag={() => {
        onDragStateChange(false)
      }}
    >
      {images.map((image, index) => (
        <CompDetailImage
          key={`${comp.arvcomp?.id}-image-${index}`}
          uri={image}
          containerWidth={width}
          index={index}
          imageCount={images.length}
          onPress={() => {
            onImagePress(index)
          }}
        />
      ))}
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  scrollViewContainer: {
    height: Platform.select({ web: "100%" }),
    alignItems: "center",
    paddingBottom: 260,
  },
  image: {
    minHeight: 280,
    // aspectRatio: 16 / 9,
    borderRadius: 6,
    alignSelf: "center",
  },
})

export default CompDetailImagesList
